import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import SeletorDeAtendente from "../SeletorDeAtendente";

// Fase C5 — para onde vai a chamada. Mesmo par do TransferTicketModal
// (atendente com busca no servidor, ou fila), só que exatamente UM dos dois:
// transferir "para os dois" não tem semântica na chamada.

const ModalDeTransferencia = ({ aberto, callId, aoFechar, aoTransferir }) => {
	const [filas, setFilas] = useState([]);
	const [usuario, setUsuario] = useState(null);
	const [fila, setFila] = useState("");
	const [enviando, setEnviando] = useState(false);

	const { findAll: listarFilas } = useQueues();

	// Quem pode RECEBER esta chamada vem PRONTO do servidor, já em página: é a
	// mesma consulta que lista atendente, recortada pelo `membrosDaFila` (a
	// regra da distribuição automática). Antes a janela pedia os ids, mandava a
	// lista de volta pela URL e peneirava no navegador — filtro em dois lugares,
	// teto de ids e uma lista que podia ficar da chamada anterior.
	const candidatosDaChamada = useCallback(
		async ({ searchParam, pageNumber }, signal) => {
			const { data } = await api.get(`/calls/${callId}/transfer-targets`, {
				params: { searchParam: searchParam || undefined, pageNumber },
				signal,
			});

			return data;
		},
		[callId]
	);

	useEffect(() => {
		if (!aberto) return;

		(async () => {
			try {
				setFilas(await listarFilas());
			} catch (err) {
				toastError(err);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [aberto]);

	const fechar = () => {
		setUsuario(null);
		setFila("");
		aoFechar();
	};

	const enviar = async (e) => {
		e.preventDefault();

		if (!usuario && !fila) return;

		setEnviando(true);

		// Um destino só: escolher atendente ignora a fila, e é o que a pessoa
		// espera — o nome é mais específico que o grupo.
		const deuCerto = await aoTransferir(
			usuario ? { userId: usuario.id } : { queueId: Number(fila) }
		);

		setEnviando(false);

		if (deuCerto) fechar();
	};

	return (
		<Dialog open={aberto} onOpenChange={(o) => !o && fechar()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Transferir chamada</DialogTitle>
					<DialogDescription>
						A chamada continua com você até o destino aceitar. O cliente não
						percebe nada enquanto isso.
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={enviar} className="space-y-4">
					<div className="space-y-1.5">
						<Label>Atendente</Label>
						<SeletorDeAtendente
							aberto={aberto}
							valor={usuario}
							fonte={candidatosDaChamada}
							textoSemNinguem="Ninguém disponível para receber a chamada agora. Transfira para um departamento abaixo."
							aoEscolher={(u) => {
								setUsuario(u);
								setFila("");
							}}
						/>
						<p className="text-xs text-muted-foreground">
							Só aparece quem pode atender chamada agora: online, com permissão
							de chamadas e no departamento desta ligação.
						</p>
					</div>

					<div className="space-y-1.5">
						<Label>Ou um departamento</Label>
						<Select
							value={fila ? String(fila) : "none"}
							onValueChange={(v) => {
								setFila(v === "none" ? "" : v);
								if (v !== "none") setUsuario(null);
							}}
						>
							<SelectTrigger>
								<SelectValue placeholder="Escolher departamento" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="none">Nenhuma</SelectItem>
								{filas.map((f) => (
									<SelectItem key={f.id} value={String(f.id)}>
										{f.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="text-xs text-muted-foreground">
							O departamento oferece a chamada a um atendente livre pela estratégia
							configurada.
						</p>
					</div>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={fechar}
							disabled={enviando}
						>
							Cancelar
						</Button>
						<Button type="submit" disabled={enviando || (!usuario && !fila)}>
							{enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
							Transferir
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default ModalDeTransferencia;
